import { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { format, parse } from "date-fns";

/**
 * Parsează textul introdus într-un format yyyy-MM-dd.
 * Acceptă: dd/MM/yyyy, dd.MM.yyyy, dd-MM-yyyy, yyyy-MM-dd
 */
export const parseInputDate = (text) => {
  if (!text) return "";
  const cleanText = text.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanText)) return cleanText;
  const match = cleanText.match(/^(\d{1,2})[\/\.\-](\d{1,2})[\/\.\-](\d{4})$/);
  if (match) {
    const day = match[1].padStart(2, '0');
    const month = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  }
  return cleanText;
};

/**
 * Afișează data din format yyyy-MM-dd în dd/MM/yyyy.
 */
export const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "dd/MM/yyyy");
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

/**
 * Input de dată cu auto-format: doar cifre + "/", auto-inserare "/" după zi și lună.
 * value: yyyy-MM-dd (intern) | onChange: (yyyy-MM-dd) => void
 */
export function DateAutoInput({ value, onChange, placeholder = "dd/MM/yyyy", disabled, className, ...rest }) {
  const displayVal = formatDateForDisplay(value);
  const [localText, setLocalText] = useState(displayVal);
  const prevLenRef = useRef(displayVal.length);

  useEffect(() => {
    const newDisplay = formatDateForDisplay(value);
    if (newDisplay !== localText) {
      setLocalText(newDisplay);
      prevLenRef.current = newDisplay.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleInput = (e) => {
    const raw = e.target.value;
    let cleaned = raw.replace(/[^0-9/]/g, '');
    cleaned = cleaned.replace(/^\//, '').replace(/\/\//g, '/');

    // Auto-insert "/" when typing forward
    if (cleaned.length > prevLenRef.current) {
      const digits = cleaned.replace(/\//g, '');
      if (digits.length >= 2 && cleaned.length === 2 && !cleaned.includes('/')) {
        cleaned += '/';
      } else if (cleaned.split('/').length === 2) {
        const parts = cleaned.split('/');
        if (parts[1] && parts[1].length === 2 && cleaned.length === 5) {
          cleaned += '/';
        }
      }
    }

    if (cleaned.length > 10) cleaned = cleaned.slice(0, 10);
    prevLenRef.current = cleaned.length;
    setLocalText(cleaned);

    if (cleaned === "") {
      onChange("");
      return;
    }

    // Only update parent when we have a complete valid date
    const parsed = parseInputDate(cleaned);
    if (/^\d{4}-\d{2}-\d{2}$/.test(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <Input
      type="text"
      value={localText}
      onChange={handleInput}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      maxLength={10}
      data-testid={rest["data-testid"]}
    />
  );
}

export default DateAutoInput;
